import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { Card, CardContent } from "./ui/Card";
import { Spinner } from "./ui/Spinner";
import { useToast } from "./ui/Toast";
import { AlertCircle, Check, X } from 'lucide-react';
import { Switch } from "./ui/Switch";
import { useUsersApi } from '../apis/users-apis';
import { UserData } from '../types';

interface UserDetailModalProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserUpdated: () => void;
}

interface EditableValues {
  isActive: boolean;
  isAdmin: boolean;
}

// Helper functions to extract complex conditions and logic
const shouldFetchUser = (open: boolean, userId: string | null): boolean => {
  return open && !!userId;
};

const canSaveUser = (user: UserData | null, userId: string | null): boolean => {
  return !!user && !!userId;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

const getStatusBadgeClass = (isActive: boolean): string => {
  return isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800";
};

const getRoleBadgeClass = (isAdmin: boolean): string => {
  return isAdmin ? "border-blue-500 text-blue-800" : "border-gray-500 text-gray-800";
};

// Extracted components for better separation of concerns
const LoadingState: React.FC<{ open: boolean; onOpenChange: (open: boolean) => void }> = ({ 
  open, 
  onOpenChange 
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <div className="flex items-center justify-center p-6">
        <Spinner className="w-8 h-8" />
      </div>
    </DialogContent>
  </Dialog>
);

const ErrorState: React.FC<{ 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  error: string 
}> = ({ open, onOpenChange, error }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <div className="flex items-center gap-2 text-red-500 p-4">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    </DialogContent>
  </Dialog>
);

const StatusField: React.FC<{
  isEditing: boolean;
  isActive: boolean;
  editedIsActive: boolean;
  isSaving: boolean;
  onStatusChange: (checked: boolean) => void;
}> = ({ isEditing, isActive, editedIsActive, isSaving, onStatusChange }) => {
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Switch
          checked={editedIsActive}
          onCheckedChange={onStatusChange}
          disabled={isSaving}
        />
        <span>{editedIsActive ? 'Active' : 'Inactive'}</span>
      </div>
    );
  }

  return (
    <Badge 
      variant={isActive ? "default" : "secondary"}
      className={getStatusBadgeClass(isActive)}
    >
      {isActive ? "Active" : "Inactive"}
    </Badge>
  );
};

const RoleField: React.FC<{
  isEditing: boolean;
  isAdmin: boolean;
  editedIsAdmin: boolean;
  isSaving: boolean;
  onRoleChange: (checked: boolean) => void;
}> = ({ isEditing, isAdmin, editedIsAdmin, isSaving, onRoleChange }) => {
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Switch
          checked={editedIsAdmin}
          onCheckedChange={onRoleChange}
          disabled={isSaving}
        />
        <span>{editedIsAdmin ? 'Admin' : 'User'}</span>
      </div>
    );
  }

  return (
    <Badge 
      variant="outline"
      className={getRoleBadgeClass(isAdmin)}
    >
      {isAdmin ? "Admin" : "User"}
    </Badge>
  );
};

const EditButtons: React.FC<{
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ isEditing, isSaving, onEdit, onCancel, onSave }) => {
  if (isEditing) {
    return (
      <>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner className="w-4 h-4 mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </>
    );
  }

  return (
    <Button onClick={onEdit}>
      Edit User
    </Button>
  );
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  open,
  onOpenChange,
  onUserUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<EditableValues>({
    isActive: false,
    isAdmin: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();
  const { getUser, updateUser } = useUsersApi();

  // Extracted data fetching logic
  const fetchUserDetails = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getUser(userId);
      setUser(data);
      setEditedValues({
        isActive: data.isActive,
        isAdmin: data.isAdmin
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user details';
      setError(errorMessage);
      addToast({
        title: "Error",
        description: "Failed to load user details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Extracted save logic
  const handleSave = async () => {
    if (!canSaveUser(user, userId)) return;
    
    setIsSaving(true);
    try {
      await updateUser(userId!, {
        isActive: editedValues.isActive,
        isAdmin: editedValues.isAdmin
      });

      addToast({
        title: "Success",
        description: "User updated successfully",
        variant: "default",
      });

      const updatedUser = await getUser(userId!);
      setUser(updatedUser);
      
      onUserUpdated();
      setIsEditing(false);
    } catch (err) {
      addToast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Extracted event handlers
  const handleEdit = () => setIsEditing(true);
  
  const handleCancel = () => {
    if (!user) return;
    setIsEditing(false);
    setEditedValues({
      isActive: user.isActive,
      isAdmin: user.isAdmin
    });
  };

  const handleStatusChange = (checked: boolean) => {
    setEditedValues(prev => ({ ...prev, isActive: checked }));
  };

  const handleRoleChange = (checked: boolean) => {
    setEditedValues(prev => ({ ...prev, isAdmin: checked }));
  };
  
  useEffect(() => {
    if (shouldFetchUser(open, userId)) {
      fetchUserDetails();
    }
  }, [userId, open, addToast]);

  // Early returns for loading and error states
  if (loading) {
    return <LoadingState open={open} onOpenChange={onOpenChange} />;
  }

  if (error) {
    return <ErrorState open={open} onOpenChange={onOpenChange} error={error} />;
  }

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Email:</span>
                  <span>{user.email}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Status:</span>
                  <StatusField
                    isEditing={isEditing}
                    isActive={user.isActive}
                    editedIsActive={editedValues.isActive}
                    isSaving={isSaving}
                    onStatusChange={handleStatusChange}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Role:</span>
                  <RoleField
                    isEditing={isEditing}
                    isAdmin={user.isAdmin}
                    editedIsAdmin={editedValues.isAdmin}
                    isSaving={isSaving}
                    onRoleChange={handleRoleChange}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Last Login:</span>
                  <span>{formatDate(user.lastLogin)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Created At:</span>
                  <span>{formatDate(user.createdAt)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 pt-2">
            <EditButtons
              isEditing={isEditing}
              isSaving={isSaving}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;